import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FormNavigation from 'components/FormNavigation';
import { FlowComponentType } from 'routes/types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'handlers';
import { YourDetailsVariable, YourSSN4Variable } from 'enums/LoanFormVariables';
import FormContainer from 'components/LoanForm/FormContainer';
import { setYourSSN4 } from 'handlers/yourSSN4';
import Button from 'components/Button';
import { useForm } from 'react-hook-form';
import { getMessageForInvalidFields, getMessageForRequiredFields } from 'utils/errors';
import Input from 'components/Input';
import { YourIncomeVariant } from 'handlers/yourIncome';
import { YourSSN4Result } from 'enums/FlowNextResults';
import { getYourIncomeVariant } from 'selectors/yourIncome';
import { getYourContactData } from 'selectors/getYourContact';
import { CreateAuthSessionResponse, ErrorType as MethodErrorType } from 'api/MethodApi';
import { getAuthData } from 'selectors/getAuthData';
import { getYourBirthDateData } from 'selectors/yourBirthDate';
import { getYourNameData } from 'selectors/yourName';
import { getYourAddressData } from 'selectors/yourAddress';
import { authSessionThunk } from 'thunks';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import useMethodAuth from 'hooks/useMethodAuth';
import { getMethodAuthData } from 'selectors/methodAuth';

const SOCIAL_SECURITY_LENGTH_PATTERN = /^.{4}$/;

export enum YourSSN4InputLabel {
  SSN4 = 'Last 4 digits',
}

const YourSSN = ({ navigationInfo, handleNext }: FlowComponentType): JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { processMethodErrorAndRedirect } = useMethodAuth();

  const { SSN4 } = useSelector((state: RootState) => state.yourSSN4);
  const { phone_number: phoneNumber } = useSelector(getYourContactData);
  const { first_name: firstName, last_name: lastName } = useSelector(getYourNameData);
  const { birth_date: birthDate } = useSelector(getYourBirthDateData);
  const { sessionToken } = useSelector(getAuthData);
  const yourAddress = useSelector(getYourAddressData);

  const { isLoading } = useSelector(getMethodAuthData);
  const variant = useSelector(getYourIncomeVariant);

  const {
    formState: { errors, isValid },
    register,
    watch,
    trigger,
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      [YourSSN4Variable.SSN4]: SSN4,
    },
  });

  const watcher = watch();

  useEffect(() => {
    register(YourSSN4Variable.SSN4, {
      required: getMessageForRequiredFields(YourSSN4InputLabel.SSN4),
      pattern: {
        message: getMessageForInvalidFields(YourSSN4InputLabel.SSN4),
        value: SOCIAL_SECURITY_LENGTH_PATTERN,
      },
    });
  });

  const handleContinue = async () => {
    dispatch(
      setYourSSN4({
        [YourSSN4Variable.SSN4]: watcher[YourSSN4Variable.SSN4],
      }),
    );

    try {
      await dispatchWithUnwrap(
        authSessionThunk({
          phoneNumber,
          firstName,
          lastName,
          birthDate,
          address: {
            street: `${yourAddress[YourDetailsVariable.AddressLine1] ?? ''} ${
              yourAddress[YourDetailsVariable.AddressLine2] ?? ''
            }`,
            city: yourAddress[YourDetailsVariable.City],
            stateOrProvince: yourAddress[YourDetailsVariable.ArgyleState],
            zipOrPostalCode: yourAddress[YourDetailsVariable.ZipCode],
          },
          sessionToken: sessionToken!,
          SSN4: watcher[YourSSN4Variable.SSN4],
        }),
      );
    } catch (e) {
      const { errorType, entityId: currentEntityId } = e as CreateAuthSessionResponse;

      if (processMethodErrorAndRedirect(handleNext, errorType, false, true, currentEntityId)) {
        if (errorType === MethodErrorType.InvalidZipCode) navigate(-1);
        return;
      }
    }

    if (variant === YourIncomeVariant.CardCrossSell) {
      handleNext(YourSSN4Result.CardCrossSell);
    } else {
      handleNext();
    }
  };

  return (
    <>
      <FormNavigation {...navigationInfo} />
      <FormContainer title="Confirm last 4 of your SSN" subtitle="We need this to pull your data.">
        <Input
          label={YourSSN4InputLabel.SSN4}
          placeholder="Last 4 digits of your Social Security number"
          errorMessage={errors[YourSSN4Variable.SSN4]?.message}
          name={YourSSN4InputLabel.SSN4}
          onChange={(event: React.FocusEvent<HTMLInputElement>) => {
            setValue(YourSSN4Variable.SSN4, event.target.value.trim());
            trigger(YourSSN4Variable.SSN4);
          }}
          value={watcher[YourSSN4Variable.SSN4]}
          onKeyUp={(e) => e.key === 'Enter' && isValid && handleContinue()}
          type="number"
          disabled={isLoading}
          autoFocus
        />

        <Button
          onClick={handleContinue}
          disabled={!isValid || watcher[YourSSN4Variable.SSN4].length !== 4}
          isLoading={isLoading}
        >
          Next
        </Button>
      </FormContainer>
    </>
  );
};

export default YourSSN;
